import { inject, Injectable } from '@angular/core';
import { ROOT_EFFECTS_INIT, Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  filter,
  map,
  of,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs';
import { ReferenceService } from '../../shared/services/reference.service';
import { ToastService } from '../../shared/services/toast.service';
import { AuthActions } from '../auth/auth.actions';
import { selectCurrentHotelId } from '../auth/auth.selectors';
import { HotelInformationActions } from './hotel-information.actions';

@Injectable()
export class HotelInformationEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly referenceService = inject(ReferenceService);
  private readonly toast = inject(ToastService);

  readonly initialize$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROOT_EFFECTS_INIT),
      withLatestFrom(this.store.select(selectCurrentHotelId)),
      filter(([, hotelId]) => !!hotelId),
      map(() => HotelInformationActions.load()),
    ),
  );

  readonly authChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AuthActions.loginSuccess,
        AuthActions.tokenLoginSuccess,
        AuthActions.changeHotelSuccess,
      ),
      map(() => HotelInformationActions.load()),
    ),
  );

  readonly load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HotelInformationActions.load),
      switchMap(() =>
        this.referenceService.getHotelInformation().pipe(
          takeUntil(
            this.actions$.pipe(
              ofType(HotelInformationActions.clear, AuthActions.logout),
            ),
          ),
          map((hotelInformation) =>
            HotelInformationActions.loadSuccess({ hotelInformation }),
          ),
          catchError((error: Error) =>
            of(
              HotelInformationActions.loadFailure({
                error: error.message || 'Hotel information could not be loaded',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly loadFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(HotelInformationActions.loadFailure),
        tap(({ error }) => this.toast.error(error)),
      ),
    { dispatch: false },
  );
}
